/**
 * Sales / Prospecting endpoints (June 2026) — outbound sales working-area.
 *
 * BE source of truth: ``ando-agent/src/api/v1/endpoints/prospecting.py``.
 * Ando is a system of action, NOT a marketing tool: these power the Sales
 * working-area (campaigns + ICP discovery + cold-email HITL + suppression).
 * The vendor behind discovery is never exposed (white-label web-research).
 */

import { apiClient } from "../ApiClient.js";

// ─── Types ───────────────────────────────────────────────────────────────

export type ProspectStatus =
  | "new"
  | "drafted"
  | "contacted"
  | "replied"
  | "bounced"
  | "suppressed";

/** Manual sales qualification — orthogonal to the outreach ``status`` (a lead
 * can be both "contacted" and "qualified"). */
export type ProspectQualification = "none" | "qualified" | "disqualified";

/** Effective role of the viewer on a campaign (collaboration ACL). */
export type CampaignRole = "owner" | "editor" | "viewer";

export interface SalesCampaign {
  id: string;
  name: string;
  icp_brief: string;
  status: "active" | "archived";
  /** Org-wide sharing flag — when true, every org member is an editor. */
  org_shared?: boolean;
  /** The viewer's effective role (owner | editor | viewer). */
  my_role?: CampaignRole;
  owner_name?: string | null;
  owner_email?: string | null;
  /** Number of explicit per-user collaborators (excludes org-wide share). */
  collaborator_count?: number;
  user_id?: string;
  created_at?: string | null;
  updated_at?: string | null;
}

/** A member of the org — for the people-picker (sharing) + assignee dropdown. */
export interface TeamMember {
  user_id: string;
  name?: string | null;
  email?: string | null;
}

/** One per-user ACL entry on a campaign. */
export interface Collaborator {
  user_id: string;
  name?: string | null;
  email?: string | null;
  role: "editor" | "viewer";
}

export interface CampaignSharing {
  org_shared: boolean;
  my_role: CampaignRole;
  owner_id: string;
  collaborators: Collaborator[];
}

/** Activity-timeline event type (unified feed: structured events + notes). */
export type ActivityType =
  | "created"
  | "status_changed"
  | "qualification_changed"
  | "assigned"
  | "follow_up_set"
  | "note"
  | "email_sent"
  | "shared"
  | "field_edited";

export interface ProspectActivity {
  id: string;
  prospect_id?: string | null;
  actor_id?: string | null;
  actor_name?: string | null;
  actor_email?: string | null;
  activity_type: ActivityType;
  body?: string | null;
  payload?: Record<string, unknown>;
  created_at?: string | null;
  updated_at?: string | null;
  /** Only on the cross-campaign overview feed. */
  campaign_name?: string | null;
}

/** Aggregate CRM counters for the Sales "Panoramica" landing. */
export interface SalesOverviewStats {
  total: number;
  qualified: number;
  disqualified: number;
  to_contact: number;
  contacted: number;
  missing_email: number;
  mine: number;
  overdue: number;
  due_week: number;
}

export interface SalesOverview {
  stats: SalesOverviewStats;
  campaigns: SalesCampaign[];
  recent_activity: ProspectActivity[];
}

/** Manually add a lead to a campaign (native-CRM create). */
export interface CreateProspectInput {
  company_name: string;
  domain?: string;
  contact_name?: string;
  contact_email?: string;
  contact_role?: string;
}

export interface Prospect {
  id: string;
  campaign_id: string;
  company_name: string;
  domain?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_role?: string | null;
  /** AI fit-score 0-100 (string from the numeric column, or number). */
  score?: number | string | null;
  score_rationale?: string | null;
  source_urls?: string[] | null;
  status: ProspectStatus;
  /** Manual qualify/disqualify decision, persisted (default "none"). */
  qualification?: ProspectQualification | null;
  /** Free-text mini-CRM notes the user can edit from the lead detail drawer. */
  notes?: string | null;
  /** Team member responsible for this lead (mini-CRM). */
  assignee_id?: string | null;
  assignee_name?: string | null;
  assignee_email?: string | null;
  /** Next follow-up timestamp (ISO) — drives "Due today / This week". */
  follow_up_at?: string | null;
  /** Short free-text of the planned next action. */
  next_step?: string | null;
  /** CRM bridge (Jul 2026): the crm_deals.id this lead was converted into
   * (``POST /crm/prospects/{id}/convert``). NULL = not converted yet. */
  converted_deal_id?: string | null;
  /**
   * Optional enrichment the discovery pipeline attaches per prospect — a JSONB
   * blob whose fields are all optional strings. Drives the leads table
   * "Descrizione" column + the lead detail drawer ("why reach out now", sector,
   * location, size hint).
   */
  enrichment?: {
    description?: string | null;
    sector?: string | null;
    location?: string | null;
    size_hint?: string | null;
    signal?: string | null;
  } | null;
  created_at?: string | null;
}

export interface SuppressionEntry {
  id: string;
  email_or_domain: string;
  reason?: string | null;
  created_at?: string | null;
}

/** One connected account that satisfies a capability (account-picker-first). */
export interface AccountConnection {
  connection_id: string;
  provider: string;
  email?: string | null;
  label: string;
  /** Authoritative "connection broken" signal (NOT token expiry). */
  needs_reauth: boolean;
}

export interface ProspectDiscoveryResponse {
  status: string;
  campaign_id: string;
}

/** Minimal projection of the ActionProposal the draft endpoint returns. */
export interface ColdEmailProposalRef {
  proposal_id: string;
  action_type: string;
  title: string;
}

export interface DraftColdEmailInput {
  instructions?: string;
  provider?: "gmail" | "outlook";
  from_account?: string;
  account_connection_id?: string;
}

// ─── Campaigns ─────────────────────────────────────────────────────────────

/** GET ``/sales/campaigns`` — my campaigns, newest first. */
export async function listCampaigns(): Promise<SalesCampaign[]> {
  return await apiClient.get<SalesCampaign[]>("/sales/campaigns");
}

/** POST ``/sales/campaigns`` — create a campaign from an ICP brief. */
export async function createCampaign(input: {
  name: string;
  icp_brief?: string;
}): Promise<SalesCampaign> {
  return await apiClient.post<SalesCampaign>("/sales/campaigns", input);
}

/** GET ``/sales/campaigns/{id}/prospects`` — leads, optional status filter. */
export async function listProspects(
  campaignId: string,
  status?: ProspectStatus,
): Promise<Prospect[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return await apiClient.get<Prospect[]>(
    `/sales/campaigns/${encodeURIComponent(campaignId)}/prospects${qs}`,
  );
}

/** Manual mini-CRM edit of a single lead. Only keys PRESENT are patched; an
 * explicit ``null`` on assignee_id / follow_up_at clears the field. */
export interface UpdateProspectInput {
  status?: ProspectStatus;
  qualification?: ProspectQualification;
  contact_email?: string;
  contact_name?: string;
  notes?: string;
  assignee_id?: string | null;
  follow_up_at?: string | null;
  next_step?: string | null;
  /** Client-only optimistic hints (denormalized assignee for instant UI); the
   * BE ignores unknown fields — they keep the optimistic cache coherent so the
   * row/drawer don't flash the previous assignee's name during the round-trip. */
  assignee_name?: string | null;
  assignee_email?: string | null;
}

/** PATCH ``/sales/prospects/{id}`` — manually edit a lead's status / contact / notes. */
export async function updateProspect(
  prospectId: string,
  input: UpdateProspectInput,
): Promise<{ status: string }> {
  return await apiClient.patch<{ status: string }>(
    `/sales/prospects/${encodeURIComponent(prospectId)}`,
    input,
  );
}

// ─── Overview (Panoramica landing) + manual native-CRM create ───────────────

/** GET ``/sales/overview`` — aggregate KPIs + campaigns + recent team activity. */
export async function getSalesOverview(): Promise<SalesOverview> {
  return await apiClient.get<SalesOverview>("/sales/overview");
}

/** POST ``/sales/campaigns/{id}/prospects`` — manually add a lead (native CRM). */
export async function createProspect(
  campaignId: string,
  input: CreateProspectInput,
): Promise<{ status: string; id: string }> {
  return await apiClient.post<{ status: string; id: string }>(
    `/sales/campaigns/${encodeURIComponent(campaignId)}/prospects`,
    input,
  );
}

// ─── Collaboration — team, sharing (org flag + per-user ACL) ─────────────────

/** GET ``/sales/team`` — active org members for the people-picker + assignee. */
export async function listTeam(): Promise<TeamMember[]> {
  return await apiClient.get<TeamMember[]>("/sales/team");
}

/** GET ``/sales/campaigns/{id}/collaborators`` — org flag + per-user ACL. */
export async function getCampaignSharing(campaignId: string): Promise<CampaignSharing> {
  return await apiClient.get<CampaignSharing>(
    `/sales/campaigns/${encodeURIComponent(campaignId)}/collaborators`,
  );
}

/** PATCH ``/sales/campaigns/{id}/share`` — toggle org-wide sharing (owner only). */
export async function setCampaignSharing(
  campaignId: string,
  orgShared: boolean,
): Promise<{ status: string; org_shared: boolean }> {
  return await apiClient.patch<{ status: string; org_shared: boolean }>(
    `/sales/campaigns/${encodeURIComponent(campaignId)}/share`,
    { org_shared: orgShared },
  );
}

/** POST ``/sales/campaigns/{id}/collaborators`` — share with a member at a role. */
export async function addCollaborator(
  campaignId: string,
  userId: string,
  role: "editor" | "viewer",
): Promise<{ status: string }> {
  return await apiClient.post<{ status: string }>(
    `/sales/campaigns/${encodeURIComponent(campaignId)}/collaborators`,
    { user_id: userId, role },
  );
}

/** DELETE ``/sales/campaigns/{id}/collaborators/{memberId}`` — revoke access. */
export async function removeCollaborator(
  campaignId: string,
  memberId: string,
): Promise<{ status: string }> {
  return await apiClient.delete<{ status: string }>(
    `/sales/campaigns/${encodeURIComponent(campaignId)}/collaborators/${encodeURIComponent(memberId)}`,
  );
}

// ─── Tracking — activity timeline + threaded notes ───────────────────────────

/** GET ``/sales/prospects/{id}/activities`` — a lead's timeline + notes. */
export async function listProspectActivities(
  prospectId: string,
): Promise<ProspectActivity[]> {
  return await apiClient.get<ProspectActivity[]>(
    `/sales/prospects/${encodeURIComponent(prospectId)}/activities`,
  );
}

/** POST ``/sales/prospects/{id}/notes`` — add an attributed note. */
export async function addProspectNote(
  prospectId: string,
  body: string,
): Promise<ProspectActivity> {
  return await apiClient.post<ProspectActivity>(
    `/sales/prospects/${encodeURIComponent(prospectId)}/notes`,
    { body },
  );
}

/** PATCH ``/sales/notes/{activityId}`` — edit your own note. */
export async function editProspectNote(
  activityId: string,
  body: string,
): Promise<{ status: string }> {
  return await apiClient.patch<{ status: string }>(
    `/sales/notes/${encodeURIComponent(activityId)}`,
    { body },
  );
}

/** DELETE ``/sales/notes/{activityId}`` — delete your own note. */
export async function deleteProspectNote(
  activityId: string,
): Promise<{ status: string }> {
  return await apiClient.delete<{ status: string }>(
    `/sales/notes/${encodeURIComponent(activityId)}`,
  );
}

/**
 * POST ``/sales/campaigns/{id}/enrich-emails`` — kick off a background pass that
 * finds public emails for the campaign's email-less leads. Returns immediately;
 * filled emails stream in (the prospects query already polls every 10s).
 */
export async function enrichCampaignEmails(
  campaignId: string,
): Promise<{ status: string; campaign_id: string }> {
  return await apiClient.post<{ status: string; campaign_id: string }>(
    `/sales/campaigns/${encodeURIComponent(campaignId)}/enrich-emails`,
  );
}

/** POST ``/sales/campaigns/{id}/discover`` — dispatch async ICP discovery. */
export async function discoverProspects(
  campaignId: string,
  count = 20,
): Promise<ProspectDiscoveryResponse> {
  return await apiClient.post<ProspectDiscoveryResponse>(
    `/sales/campaigns/${encodeURIComponent(campaignId)}/discover`,
    { count },
  );
}

/**
 * POST ``/sales/campaigns/{id}/deep-research`` — costly ad-hoc escalation that
 * runs an autonomous deep-research agent (minutes-scale, far costlier than
 * ``discover``) to surface harder-to-find, better-qualified leads with
 * decision-maker contacts. Returns immediately ({status:"researching"}); leads
 * stream in via the polling prospects query.
 */
export async function deepResearchProspects(
  campaignId: string,
  count = 20,
): Promise<ProspectDiscoveryResponse> {
  return await apiClient.post<ProspectDiscoveryResponse>(
    `/sales/campaigns/${encodeURIComponent(campaignId)}/deep-research`,
    { count },
  );
}

// ─── Cold email (HITL) ───────────────────────────────────────────────────

/** POST ``/sales/prospects/{id}/draft`` — build an editable cold-email proposal. */
export async function draftColdEmail(
  prospectId: string,
  input: DraftColdEmailInput = {},
): Promise<ColdEmailProposalRef> {
  return await apiClient.post<ColdEmailProposalRef>(
    `/sales/prospects/${encodeURIComponent(prospectId)}/draft`,
    input,
  );
}

// ─── Bulk cold-send ────────────────────────────────────────────────────────

export interface BulkSendInput {
  /** Selected prospect ids (suppressed / no-email rows excluded client-side). */
  prospect_ids: string[];
  /** Library template to render per-prospect (BE re-renders merge fields). */
  template_id?: string;
  /** Ad-hoc subject override when no template is chosen. */
  subject?: string;
  /** Ad-hoc body override when no template is chosen. */
  body?: string;
  provider: "gmail" | "outlook";
  /** Optional sending account (connection_id) for the chosen provider. */
  from_account?: string;
  append_signature?: boolean;
  /** Optional manual signature override (a specific pool signature the operator
   *  picked). When omitted, the worker resolves the default for `from_account`. */
  signature_id?: string | null;
}

export interface BulkSendResponse {
  status: "queued";
  count: number;
}

/**
 * POST ``/sales/campaigns/{id}/bulk-send`` — queue a throttled, background
 * cold-email send to the selected prospects. The BE re-renders the template
 * merge fields per recipient and re-checks the suppression list at send time.
 */
export async function bulkSend(
  campaignId: string,
  input: BulkSendInput,
): Promise<BulkSendResponse> {
  return await apiClient.post<BulkSendResponse>(
    `/sales/campaigns/${encodeURIComponent(campaignId)}/bulk-send`,
    input,
  );
}

// ─── Suppression (GDPR opt-out) ────────────────────────────────────────────

/** GET ``/sales/suppression`` — tenant opt-out / bounce list. */
export async function listSuppression(): Promise<SuppressionEntry[]> {
  return await apiClient.get<SuppressionEntry[]>("/sales/suppression");
}

/** POST ``/sales/suppression`` — add an email or domain to the opt-out list. */
export async function addSuppression(input: {
  email_or_domain: string;
  reason?: string;
}): Promise<{ status: string }> {
  return await apiClient.post<{ status: string }>("/sales/suppression", input);
}

// ─── Account picker ──────────────────────────────────────────────────────

/** GET ``/accounts/connections?capability=`` — accounts for the action picker. */
export async function listCapabilityAccounts(
  capability = "email_send",
): Promise<AccountConnection[]> {
  return await apiClient.get<AccountConnection[]>(
    `/accounts/connections?capability=${encodeURIComponent(capability)}`,
  );
}
